import { StyleSheet, css } from 'aphrodite'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import HeroSection from '../components/HeroSection'
import InfoGrid from '../components/InfoGrid'
import LandingSection from '../components/LandingSection'
import LandingFooter from '../components/navigation/LandingFooter'
import LandingHeader from '../components/navigation/LandingHeader'

export default function DigitalEcosystemPage() {
  const navigate = useNavigate()

  const handleJoinCommunityClick = () => {
    window.scrollTo(0, 0)
    navigate('/register')
  }

  return (
    <div className={css(styles.container)}>
      <LandingHeader />
      <HeroSection
        subtitle="The Digital Ecosystem"
        title="TECHNOLOGY BUILT TO SERVE"
        description="The Ecosystem’s technology makes every hauling project faster and easier. At its core is a modular infrastructure — a combination of robust architecture, cutting-edge technology, and bold, intuitive interfaces. Together, they form a powerful foundation of the Ecosystem dedicated to the Dump Truck Industry."
        imageSrc="/ecosystem_hero.webp"
        imageAlt="Our Digital Ecosystem"
        leftImageSrc="/left_digital_hero.webp"
        leftImageAlt="Digital Ecosystem Left"
      />
      <section className={css(styles.digitalPlatformSection)}>
        <div className={css(styles.digitalPlatformImageContainer)}>
          <img
            src="/who_we_are2.webp"
            alt="Co-creation in action"
            className={css(styles.digitalPlatformImage)}
            loading="lazy"
          />
        </div>
        <div className={css(styles.digitalPlatformContent)}>
          <h2 className={css(styles.digitalPlatformTitle)}>
            AN ECOSYSTEM FOR REAL WORK
          </h2>
          <p className={css(styles.digitalPlatformDescription)}>
            The Ecosystem is not just a tool – it is an evolving organism
            connecting the Community. Whether you are a Contractor, Broker, Sub
            Hauler, or a Driver, the Ecosystem delivers relevant information in
            real time.
          </p>
          <p className={css(styles.digitalPlatformDescription)}>
            Through automation, integration, and modular capabilities, the
            Ecosystem supports dataflows, reducing complexity and increasing
            productivity.
          </p>
          <button
            className={css(styles.getStartedButton)}
            onClick={handleJoinCommunityClick}
          >
            Join Community
          </button>
        </div>
      </section>

      <section className={css(styles.coreFocusSection)}>
        <div className={css(styles.coreFocusTextContainer)}>
          <h2 className={css(styles.coreFocusTitle)}>CORE FOCUS AREAS</h2>
          <p className={css(styles.coreFocusDescription)}>
            The architecture of the Ecosystem centers around four key areas
            creating a seamless, efficient, and environmentally responsible
            platform.
          </p>
        </div>
        <div className={css(styles.infoGridContainer)}>
          <InfoGrid
            icon="/icons/trust_icon.png"
            title="Trust-Building Mechanisms"
            description="Transparency features reinforce accountability among the Community, ensuring every transaction and interaction is built on a foundation of Trust."
          />
          <InfoGrid
            icon="/icons/workload.png"
            title="Reduced Workload"
            description="Streamlined interfaces and automations eliminate administrative burden, allowing everyone to focus on their core business rather than paperwork."
          />
          <InfoGrid
            icon="/icons/sharing.png"
            title="Real-Time Information Alerts"
            description="Live updates and dashboards connect everyone to the same hauling project instantly, ensuring all necessary parties have access to the most current and relevant information."
          />
          <InfoGrid
            icon="/icons/leaf.png"
            title="Expansion Toward Zero-Carbon Emissions"
            description="Strategic insights support efficient operations, reducing emissions in the Dump Truck Industry and moving towards a more sustainable future."
          />
        </div>
      </section>
      <LandingSection
        title="JOIN THE ECOSYSTEM"
        description="Work smarter with real-time updates, instant data access, and modular, digital templates and workflows. Cut down on paperwork, build trust, and reduce unnecessary driving – all in one Ecosystem."
        imageSrc="/ecosystem2.webp"
        imageAlt="Construction Team"
        buttonText="Join Community"
        testimonialText="Trusted by 10,000+ operators,"
        userCount="contractors and haulers"
        onButtonClick={handleJoinCommunityClick}
      />

      <LandingFooter />
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
  },
  mainContent: {
    flex: 1,
    padding: '80px 16px',
    maxWidth: '1280px',
    margin: '0 auto',
    width: '100%',
    '@media (min-width: 640px)': {
      padding: '80px 24px',
    },
    '@media (min-width: 1024px)': {
      padding: '80px 32px',
    },
  },
  digitalPlatformSection: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '32px',
    alignItems: 'center',
    padding: '40px 1rem',
    '@media (min-width: 768px)': {
      gap: '40px',
      padding: '60px 2rem',
    },
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 1fr',
      gap: '48px',
      padding: '80px 3rem',
    },
    '@media (min-width: 1280px)': {
      padding: '100px 5rem',
    },
  },
  digitalPlatformContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      gap: '20px',
    },
    '@media (min-width: 1024px)': {
      gap: '24px',
      textAlign: 'left',
    },
  },
  digitalPlatformTitle: {
    fontFamily: '"Anton", sans-serif',
    fontSize: '32px',
    fontWeight: 400,
    color: '#1F3D61',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  digitalPlatformDescription: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#4B5563',
    fontWeight: 400,
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '18px',
    },
  },
  getStartedButton: {
    backgroundColor: '#6DC3FF',
    color: '#1F407E',
    padding: '10px 24px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    alignSelf: 'center',
    width: '50%',
    '@media (min-width: 768px)': {
      padding: '11px 28px',
      borderRadius: '22px',
      fontSize: '15px',
      width: '40%',
    },
    '@media (min-width: 1024px)': {
      padding: '12px 32px',
      borderRadius: '24px',
      fontSize: '16px',
      alignSelf: 'flex-start',
      width: '30%',
    },
  },
  digitalPlatformImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    order: -1,
    '@media (min-width: 1024px)': {
      order: 0,
    },
  },
  digitalPlatformImage: {
    height: 'auto',
    maxHeight: '85vh',
    objectFit: 'cover',
    objectPosition: 'top',
    borderRadius: '24px',
    '@media (min-width: 768px)': {
      borderRadius: '24px',
      maxWidth: '90%',
    },
    '@media (min-width: 1024px)': {
      borderRadius: '24px',
      maxWidth: '100%',
    },
  },
  coreFocusSection: {
    padding: '40px 1rem',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '32px',
    alignItems: 'flex-start',
    '@media (min-width: 768px)': {
      padding: '60px 2rem',
      gap: '40px',
    },
    '@media (min-width: 1024px)': {
      padding: '80px 3rem',
      gridTemplateColumns: '1fr 2fr',
      gap: '64px',
      alignItems: 'center',
    },
    '@media (min-width: 1280px)': {
      padding: '100px 5rem',
    },
  },
  coreFocusTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      gap: '20px',
    },
    '@media (min-width: 1024px)': {
      gap: '24px',
      textAlign: 'left',
    },
  },
  coreFocusTitle: {
    fontFamily: '"Anton", sans-serif',
    fontSize: '32px',
    color: '#102E65',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  coreFocusDescription: {
    color: '#4B5563',
    fontSize: '14px',
    lineHeight: 1.6,
    fontWeight: 400,
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '18px',
    },
  },
  infoGridContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px',
    '@media (min-width: 768px)': {
      gridTemplateColumns: '1fr 1fr',
      gap: '32px',
    },
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: '1fr 1fr',
      gap: '48px',
    },
  },
})
